/**
 * Utilidades para generación automática de contenido SEO
 * Reutilizable en formularios, migraciones, seeds, etc.
 */

// Tipos para configuración SEO
export interface SeoTitleOptions {
  name: string;
  brand?: string;
  maxLength?: number;
}

export interface SeoDescriptionOptions {
  description: string;
  maxLength?: number;
}

export interface SeoKeywordsOptions {
  name: string;
  brand?: string;
  categoryName?: string;
  extra?: string[];
}

// Utilidades internas
function normalizeText(text: string): string {
  // 1) Normalizar a ASCII: convertir acentos (á, é, í, ó, ú, ü, ñ) a su base (a, e, i, o, u, u, n)
  // 2) Mantener letras y números, limpiar el resto
  const withoutDiacritics = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return withoutDiacritics
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/gi, '') // Remover caracteres especiales excepto espacios
    .replace(/\s+/g, ' ')
    .trim();
}

function dedupe(array: string[]): string[] {
  return [...new Set(array.filter(Boolean))];
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Generar título SEO automático
 * Formato: "Nombre | Marca" o solo "Nombre" si no hay marca
 */
export function generateSeoTitle({ name, brand, maxLength = 60 }: SeoTitleOptions): string {
  if (!name.trim()) return '';
  
  const cleanName = name.trim();
  const cleanBrand = brand?.trim();
  
  let title = cleanName;
  if (cleanBrand && cleanBrand !== cleanName) {
    title = `${cleanName} | ${cleanBrand}`;
  }
  
  return truncate(title, maxLength);
}

/**
 * Generar descripción SEO automática
 * Toma el primer párrafo de la descripción y lo limpia
 */
export function generateSeoDescription({ description, maxLength = 160 }: SeoDescriptionOptions): string {
  if (!description.trim()) return '';
  
  // Tomar el primer párrafo (hasta el primer salto de línea o punto)
  const firstParagraph = description
    .split('\n')[0]
    .split('.')[0]
    .trim();
  
  if (!firstParagraph) return '';
  
  // Limpiar y truncar
  const cleanDescription = firstParagraph
    .replace(/[^\w\s.,!?]/g, '') // Mantener solo letras, números, espacios y puntuación básica
    .replace(/\s+/g, ' ')
    .trim();
  
  return truncate(cleanDescription, maxLength);
}

/**
 * Generar palabras clave SEO automáticas
 * Combina palabras del nombre, marca, categoría y palabras extra
 */
export function generateSeoKeywords({ 
  name, 
  brand, 
  categoryName, 
  extra = [] 
}: SeoKeywordsOptions): string {
  const keywords: string[] = [];
  
  // Extraer palabras del nombre
  if (name.trim()) {
    const nameWords = normalizeText(name)
      .split(' ')
      .filter(word => word.length > 2); // Solo palabras de más de 2 caracteres
    keywords.push(...nameWords);
  }
  
  // Agregar marca si existe y es diferente al nombre
  if (brand?.trim() && brand.trim() !== name.trim()) {
    const brandWords = normalizeText(brand)
      .split(' ')
      .filter(word => word.length > 2);
    keywords.push(...brandWords);
  }
  
  // Agregar categoría si existe
  if (categoryName?.trim()) {
    const categoryWords = normalizeText(categoryName)
      .split(' ')
      .filter(word => word.length > 2);
    keywords.push(...categoryWords);
  }
  
  // Agregar palabras extra
  if (extra.length > 0) {
    const extraWords = extra
      .map(word => normalizeText(word))
      .filter(word => word.length > 2);
    keywords.push(...extraWords);
  }
  
  // Remover duplicados y limitar a 10 palabras clave
  const uniqueKeywords = dedupe(keywords).slice(0, 10);
  
  return uniqueKeywords.join(', ');
}

/**
 * Validar longitud de texto para SEO
 */
export function validateSeoLength(text: string, maxLength: number): {
  isValid: boolean;
  currentLength: number;
  remainingLength: number;
  isOverLimit: boolean;
} {
  const currentLength = text.length;
  const remainingLength = maxLength - currentLength;
  const isOverLimit = currentLength > maxLength;
  
  return {
    isValid: !isOverLimit,
    currentLength,
    remainingLength,
    isOverLimit,
  };
}

/**
 * Obtener sugerencias de palabras clave basadas en el contenido
 */
export function getKeywordSuggestions(content: string): string[] {
  if (!content.trim()) return [];
  
  const words = normalizeText(content)
    .split(' ')
    .filter(word => word.length > 3) // Solo palabras de más de 3 caracteres
    .filter(word => !isStopWord(word)); // Filtrar palabras vacías
  
  // Contar frecuencia de palabras
  const wordCount = words.reduce((acc, word) => {
    acc[word] = (acc[word] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Ordenar por frecuencia y devolver las más comunes
  return Object.entries(wordCount)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([word]) => word);
}

// Lista de palabras vacías en español
const STOP_WORDS = new Set([
  'el', 'la', 'de', 'del', 'en', 'y', 'a', 'que', 'es', 'se', 'no', 'te', 'lo', 'le', 'da', 'su', 'por', 'son', 'con', 'para', 'al', 'los', 'las', 'un', 'una', 'este', 'esta', 'estos', 'estas', 'pero', 'sus', 'le', 'ha', 'me', 'si', 'sin', 'sobre', 'este', 'ya', 'entre', 'cuando', 'todo', 'esta', 'ser', 'son', 'dos', 'también', 'fue', 'había', 'era', 'sido', 'estado', 'estaba', 'puede', 'pueden', 'tiene', 'tienen', 'hacer', 'hace', 'hacen', 'hecho', 'haciendo', 'muy', 'más', 'menos', 'mucho', 'poco', 'tanto', 'tan', 'como', 'donde', 'cuando', 'porque', 'aunque', 'mientras', 'desde', 'hasta', 'durante', 'antes', 'después', 'ahora', 'entonces', 'aquí', 'allí', 'donde', 'cómo', 'cuál', 'cuáles', 'quién', 'quiénes', 'qué', 'cuánto', 'cuánta', 'cuántos', 'cuántas'
]);

function isStopWord(word: string): boolean {
  return STOP_WORDS.has(word.toLowerCase());
}
